<template>
  <el-dialog
    :model-value="visible"
    width="480px"
    :show-close="false"
    :close-on-click-modal="false"
    :close-on-press-escape="closable"
    append-to-body
    align-center
    class="select-identity-dialog"
    @update:model-value="handleVisibleChange"
  >
    <div class="select-identity-body">
      <div class="dialog-header-row">
        <span class="dialog-title">{{ dialogTitle }}</span>
        <span
          v-if="closable"
          class="dialog-close"
          @click="handleClose"
        >关闭</span>
      </div>

      <div
        v-if="loading"
        class="identity-loading"
      >
        <el-icon class="is-loading">
          <Loading />
        </el-icon>
        <span>加载中...</span>
      </div>

      <template v-else>
        <!-- 个人身份：仅「切换身份」模式展示 -->
        <div
          v-if="showPersonal && personalIdentity"
          class="identity-item"
          :class="{ selected: selectedKey === PERSONAL_KEY }"
          @click="selectedKey = PERSONAL_KEY"
        >
          <img
            :src="personalAvatar"
            alt="个人头像"
            class="identity-avatar"
          >
          <div class="identity-info">
            <div class="identity-name">
              {{ personalName }}
            </div>
            <div
              v-if="personalMobile"
              class="identity-sub"
            >
              {{ personalMobile }}
            </div>
          </div>
          <span
            class="identity-radio"
            :class="{ checked: selectedKey === PERSONAL_KEY }"
          />
        </div>

        <!-- 企业身份 -->
        <div
          v-if="enterpriseList.length"
          class="enterprise-section"
        >
          <div
            v-if="showPersonal"
            class="enterprise-section-header"
          >
            <span class="section-title">企业身份</span>
            <span class="section-count">当前共{{ enterpriseList.length }}个企业身份</span>
          </div>
          <div
            v-for="item in enterpriseList"
            :key="getEntId(item)"
            class="identity-item"
            :class="{ selected: selectedKey === String(getEntId(item)) }"
            @click="selectedKey = String(getEntId(item))"
          >
            <img
              :src="getEntAvatar(item)"
              alt="企业头像"
              class="identity-avatar"
            >
            <div class="identity-info">
              <div class="identity-name">
                {{ getEntName(item) }}
              </div>
              <span
                v-if="getEntRoleLabel(item)"
                class="identity-role-tag"
              >{{ getEntRoleLabel(item) }}</span>
            </div>
            <span
              class="identity-radio"
              :class="{ checked: selectedKey === String(getEntId(item)) }"
            />
          </div>
        </div>
      </template>

      <el-button
        type="primary"
        class="confirm-btn"
        :loading="confirmLoading"
        :disabled="loading || !selectedKey"
        @click="handleConfirm"
      >
        {{ confirmButtonText }}
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import { ElMessage } from 'element-plus'
import { Loading } from '@element-plus/icons-vue'
import { switchUser, getLoginIdentities } from '@/api/login'
import { getMainBodyRoleLabel } from '@/enums/mainBodyRole'

const PERSONAL_KEY = 'personal'
const DEFAULT_AVATAR = require('@/assets/images/userDefaultAvatar.jpg')

export default {
  name: 'SelectIdentityDialog',
  components: { Loading },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    /** 登录接口直接返回的身份数据，有则不再请求 identities */
    identityData: {
      type: Object,
      default: null
    },
    /** 是否显示关闭按钮（登录强制选择时可设为 false） */
    closable: {
      type: Boolean,
      default: true
    },
    /**
     * all: 切换身份（个人+企业）
     * enterprise: 企业登录仅选企业列表
     */
    mode: {
      type: String,
      default: 'all',
      validator: (v) => ['all', 'enterprise'].includes(v)
    }
  },
  emits: ['update:visible', 'success', 'close'],
  data() {
    return {
      PERSONAL_KEY,
      loading: false,
      confirmLoading: false,
      selectedKey: '',
      personalIdentity: null,
      enterpriseList: [],
      currentMainBodyType: '',
      currentMainBodyId: ''
    }
  },
  computed: {
    showPersonal() {
      return this.mode === 'all'
    },
    dialogTitle() {
      return this.mode === 'enterprise' ? '选择企业' : '选择管理用户'
    },
    confirmButtonText() {
      return this.mode === 'enterprise' ? '确认登录' : '确认切换'
    },
    personalAvatar() {
      return this.personalIdentity?.avatar || DEFAULT_AVATAR
    },
    personalName() {
      const p = this.personalIdentity || {}
      return p.name || p.realName || p.nickName || '个人用户'
    },
    personalMobile() {
      const p = this.personalIdentity || {}
      return p.maskedMobile || p.mobile || p.phone || p.phoneNumber || ''
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.initData()
      }
    }
  },
  methods: {
    getEntId(item) {
      return item?.entId ?? item?.enterpriseId ?? item?.id ?? ''
    },
    getEntName(item) {
      return item?.entName || item?.enterpriseName || item?.name || ''
    },
    getEntAvatar(item) {
      return item?.avatar || DEFAULT_AVATAR
    },
    getEntRoleLabel(item) {
      const role = item?.role || item?.roleName || item?.mainBodyRole || ''
      if (!role) return ''
      return getMainBodyRoleLabel(role) || role
    },
    handleVisibleChange(val) {
      this.$emit('update:visible', val)
      if (!val) {
        this.$emit('close')
      }
    },
    handleClose() {
      this.$emit('update:visible', false)
      this.$emit('close')
    },
    applyIdentityPayload(data) {
      if (!data) return
      // 企业登录模式不展示个人身份
      this.personalIdentity = this.showPersonal ? (data.personalIdentity || null) : null
      this.enterpriseList = Array.isArray(data.enterpriseIdentities) ? data.enterpriseIdentities : []
      this.currentMainBodyType = data.currentMainBodyType || ''
      this.currentMainBodyId = data.currentMainBodyId != null ? String(data.currentMainBodyId) : ''

      if (this.mode === 'enterprise') {
        // 企业登录：默认选中当前企业或第一个
        if (this.currentMainBodyId && this.enterpriseList.some(e => String(this.getEntId(e)) === this.currentMainBodyId)) {
          this.selectedKey = this.currentMainBodyId
        } else if (this.enterpriseList.length) {
          this.selectedKey = String(this.getEntId(this.enterpriseList[0]))
        }
        return
      }

      // 切换身份：默认选中当前身份
      if (this.currentMainBodyType === 'enterprise' || this.currentMainBodyType === '2') {
        this.selectedKey = this.currentMainBodyId || (this.enterpriseList[0] ? String(this.getEntId(this.enterpriseList[0])) : PERSONAL_KEY)
      } else if (this.personalIdentity) {
        this.selectedKey = PERSONAL_KEY
      } else if (this.enterpriseList.length) {
        this.selectedKey = String(this.getEntId(this.enterpriseList[0]))
      }
    },
    async initData() {
      this.selectedKey = ''
      this.personalIdentity = null
      this.enterpriseList = []

      if (this.identityData) {
        this.applyIdentityPayload(this.identityData)
        return
      }

      this.loading = true
      try {
        const res = await getLoginIdentities()
        if (res && res.code === 200) {
          this.applyIdentityPayload(res.data || {})
        }
      } catch (e) {
        console.error('获取身份列表失败:', e)
      } finally {
        this.loading = false
      }
    },
    async handleConfirm() {
      if (!this.selectedKey) {
        ElMessage.warning(this.mode === 'enterprise' ? '请选择企业' : '请选择管理用户')
        return
      }

      this.confirmLoading = true
      try {
        const entId = this.selectedKey === PERSONAL_KEY ? undefined : this.selectedKey
        const response = await switchUser(entId)
        if (response && response.code === 200 && response.data?.token) {
          const { token, canSwitchIdentity } = response.data
          this.$auth.clearAuth()
          this.$auth.setToken(token)
          if (canSwitchIdentity !== undefined) {
            this.$auth.setCanSwitchIdentity(!!canSwitchIdentity)
          } else {
            this.$auth.setCanSwitchIdentity(true)
          }
          await this.$auth.refreshMainBody()
          this.$emit('update:visible', false)
          this.$emit('success', response.data)
        }
      } catch (e) {
        console.error('切换身份失败:', e)
      } finally {
        this.confirmLoading = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.select-identity-body {
  padding: 4px 8px 8px;
}

.dialog-header-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
}

.dialog-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.dialog-close {
  font-size: 14px;
  color: #909399;
  cursor: pointer;
  user-select: none;

  &:hover {
    color: #606266;
  }
}

.identity-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 40px 0;
  color: #909399;
  font-size: 14px;
}

.identity-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: background-color 0.2s;
  margin-bottom: 8px;

  &:hover {
    background-color: #f5f8ff;
  }

  &.selected {
    background-color: #ebf2ff;
  }
}

.identity-avatar {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  object-fit: cover;
  flex-shrink: 0;
  background: #f0f0f0;
}

.identity-info {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.identity-name {
  font-size: 15px;
  font-weight: 500;
  color: #1a1a1a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.identity-sub {
  font-size: 13px;
  color: #909399;
}

.identity-role-tag {
  display: inline-block;
  align-self: flex-start;
  padding: 2px 8px;
  font-size: 12px;
  color: #3b82f6;
  background: #e8f0fe;
  border-radius: 4px;
  line-height: 1.4;
}

.identity-radio {
  flex-shrink: 0;
  width: 18px;
  height: 18px;
  border: 2px solid #c0c4cc;
  border-radius: 50%;
  box-sizing: border-box;
  position: relative;
  transition: border-color 0.2s;

  &.checked {
    border-color: #3b82f6;

    &::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #3b82f6;
    }
  }
}

.enterprise-section {
  margin-top: 8px;
}

.enterprise-section-header {
  display: flex;
  align-items: baseline;
  gap: 10px;
  margin-bottom: 10px;
  padding: 0 4px;
}

.section-title {
  font-size: 14px;
  font-weight: 600;
  color: #1a1a1a;
}

.section-count {
  font-size: 12px;
  color: #909399;
}

.confirm-btn {
  width: 100%;
  height: 44px;
  margin-top: 16px;
  font-size: 16px;
  border-radius: 6px;
}
</style>

<style lang="scss">
.select-identity-dialog {
  border-radius: 12px;
  overflow: hidden;

  .el-dialog__header {
    display: none;
  }

  .el-dialog__body {
    padding: 24px 24px 20px;
  }
}
</style>
